import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

export type SplashMode = 'day' | 'night';

export interface SplashProps {
  mode?: SplashMode;
  brand?: string;
  message?: string;
  show?: boolean;
  onHidden?: () => void;
  minDuration?: number;
  fullscreen?: boolean;
}

interface Palette {
  bg: string;
  text: string;
  dim: string;
  accent: string;
  faint: string;
}

const PALETTES: Record<SplashMode, Palette> = {
  night: {
    bg: '#0a0a0f',
    text: '#f3ede0',
    dim: 'rgba(243, 237, 224, 0.55)',
    accent: '#ff8a3d',
    faint: 'rgba(243, 237, 224, 0.15)',
  },
  day: {
    bg: '#f5f1e8',
    text: '#1c1c26',
    dim: 'rgba(28, 28, 38, 0.55)',
    accent: '#d96a1f',
    faint: 'rgba(28, 28, 38, 0.12)',
  },
};

const KEYFRAMES = `
@keyframes sarvalok-pulse {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
}
@keyframes sarvalok-dot {
  0%, 100% { opacity: 0.15; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.4); }
}
`;

const FADE_MS = 600;

export const Splash = ({
  mode = 'night',
  brand = 'SARVALOK',
  message,
  show = true,
  onHidden,
  minDuration = 0,
  fullscreen = true,
}: SplashProps) => {
  const [rendered, setRendered] = useState<boolean>(show);
  const [visible, setVisible] = useState<boolean>(false);
  const [reducedMotion, setReducedMotion] = useState<boolean>(false);
  const startedAtRef = useRef<number>(Date.now());

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotionPreference = () => setReducedMotion(media.matches);
    updateMotionPreference();
    media.addEventListener?.('change', updateMotionPreference);
    return () => media.removeEventListener?.('change', updateMotionPreference);
  }, []);

  useEffect(() => {
    let raf: number | undefined;
    let t1: ReturnType<typeof setTimeout> | undefined;
    let t2: ReturnType<typeof setTimeout> | undefined;

    if (show) {
      startedAtRef.current = Date.now();
      setRendered(true);
      raf = requestAnimationFrame(() => setVisible(true));
    } else {
      const elapsed = Date.now() - startedAtRef.current;
      const wait = Math.max(0, minDuration - elapsed);
      t1 = setTimeout(() => setVisible(false), wait);
      t2 = setTimeout(() => {
        setRendered(false);
        onHidden?.();
      }, wait + FADE_MS);
    }

    return () => {
      if (raf !== undefined) cancelAnimationFrame(raf);
      if (t1 !== undefined) clearTimeout(t1);
      if (t2 !== undefined) clearTimeout(t2);
    };
  }, [show, minDuration, onHidden]);

  if (!rendered) return null;

  const p = PALETTES[mode];

  const wrapStyle: CSSProperties = {
    position: fullscreen ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: p.bg,
    color: p.text,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 32,
    zIndex: 9999,
    opacity: visible ? 1 : 0,
    transition: `opacity ${FADE_MS}ms ease-out`,
    fontFamily: '"FrauncesSplash", "Cormorant Garamond", Georgia, "Times New Roman", serif',
    pointerEvents: visible ? 'auto' : 'none',
  };

  const brandStyle: CSSProperties = {
    fontSize: 22,
    fontWeight: 500,
    letterSpacing: '0.5em',
    paddingLeft: '0.5em',
    color: p.dim,
    fontVariationSettings: '"opsz" 60',
    animation: reducedMotion ? undefined : 'sarvalok-pulse 1.8s ease-in-out infinite',
    margin: 0,
  };

  const messageStyle: CSSProperties = {
    fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
    fontSize: 10,
    fontWeight: 400,
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    color: p.dim,
    marginTop: -12,
  };

  return (
    <>
      {!reducedMotion && <style>{KEYFRAMES}</style>}
      <div
        role="status"
        aria-live="polite"
        aria-label={`Loading ${brand}`}
        style={wrapStyle}
      >
        <div style={{ width: 56, height: 56, position: 'relative' }}>
          {Array.from({ length: 8 }).map((_, i) => {
            const angle = ((i * 45 - 90) * Math.PI) / 180;
            const x = Math.cos(angle) * 22;
            const y = Math.sin(angle) * 22;
            return (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: `translate(${x}px, ${y}px)`,
                }}
              >
                <span
                  style={{
                    display: 'block',
                    width: 5,
                    height: 5,
                    marginLeft: -2.5,
                    marginTop: -2.5,
                    background: p.accent,
                    borderRadius: '50%',
                    animation: reducedMotion ? undefined : 'sarvalok-dot 1.6s ease-in-out infinite',
                    animationDelay: `${i * 0.18}s`,
                    boxShadow: mode === 'night' ? `0 0 8px ${p.accent}` : 'none',
                  }}
                />
              </div>
            );
          })}
        </div>

        <h1 style={brandStyle}>{brand}</h1>
        {message && <div style={messageStyle}>{message}</div>}
      </div>
    </>
  );
};

export default Splash;
